import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import {
  MousePointer,
  MapPin,
  Route,
  Pentagon,
  Square,
  Circle,
  Search,
  PanelLeftClose,
  PanelLeftOpen,
  Ruler,
  ClipboardPlus,
  PenLine,
  MoveDiagonal,
  Target,
  Radius,
  Download,
  FileInput,
  LandPlot,
  MousePointerClick,
  Grid3x3,
  LayoutGrid,
  ScanSearch,
  CircleDashed,
  SquareDashed,
  LocateFixed,
  Navigation,
  type LucideIcon,
} from "lucide-react";
import { useGeographyFilter } from "@/contexts/GeographyFilterContext";
import {
  OI_MAP_GRID_STEP_PRESETS,
  formatOiMapGridStepLabel,
  type OiMapGridStepMeters,
} from "@/lib/oi-map-grid";
import type { MeasureMode } from "@/components/map-libre/MapLibreMeasureTool";
import type { ContourSelectMode } from "@/components/map-libre/MapLibreContourSelectTool";
import { cn } from "@/lib/utils";

interface MapToolbarProps {
  activeTool: string | null;
  /** Визуальная подсветка (при правке контура принудительно «Выбрать», чтобы не казалось, что рисуем новый объект). */
  visualActiveTool?: string | null;
  onToolSelect: (tool: string) => void;
  disabled?: boolean;
  /** Блокировка кнопок инструментов (не кнопки сайдбара) на время правки контура */
  lockToolsForEditSession?: boolean;
  /** Открытие панели экспорта карты (PNG/PDF/...); при undefined кнопка «Скачать» не показывается. */
  onDownload?: () => void;
  /** Импорт координат (текст/файл) → preview → тот же поток, что после draw */
  onImportCoords?: () => void;
  /** Переключение правой панели общего поиска кадастров */
  onCadastreSearch?: () => void;
  /** Подсветка кнопки, когда панель поиска кадастров открыта */
  cadastreSearchActive?: boolean;
  showWorkItemCreateButton?: boolean;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  /** Сетка разграфки на карте ОИ */
  gridEnabled?: boolean;
  gridStepMeters?: OiMapGridStepMeters;
  onGridEnabledChange?: (value: boolean) => void;
  onGridStepMetersChange?: (value: OiMapGridStepMeters) => void;
  /** Панель «Пересечения с разграфкой» (номенклатура). */
  nomenclatureIntersectOpen?: boolean;
  onNomenclatureIntersectToggle?: () => void;
  /** Режим измерения (раскрытая группа у линейки). */
  measureMode?: MeasureMode;
  onMeasureModeChange?: (mode: MeasureMode) => void;
  /** Выбор объектов контуром (круг/полигон). */
  contourSelectMode?: ContourSelectMode;
  onContourSelectModeChange?: (mode: ContourSelectMode) => void;
  /** Активен фильтр списка по контуру (индикатор на кнопке). */
  contourFilterActive?: boolean;
}

type ToolbarTool = { id: string; icon: LucideIcon; label: string };

function ToolbarDivider() {
  return (
    <Separator
      orientation="vertical"
      className="mx-1.5 h-5 shrink-0 bg-border"
      decorative
    />
  );
}

function ToolButton({
  tool,
  highlighted,
  disabled,
  locked,
  onSelect,
}: {
  tool: ToolbarTool;
  highlighted: boolean;
  disabled: boolean;
  locked: boolean;
  onSelect: (id: string) => void;
}) {
  const tip = locked
    ? `${tool.label}. Завершите редактирование контура в панели на карте.`
    : tool.label;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={highlighted ? "secondary" : "ghost"}
          size="icon"
          className="h-8 w-8"
          onClick={() => onSelect(tool.id)}
          disabled={disabled || locked}
        >
          <tool.icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

export default function MapToolbar({
  activeTool,
  visualActiveTool,
  onToolSelect,
  disabled = false,
  lockToolsForEditSession = false,
  onDownload,
  onImportCoords,
  onCadastreSearch,
  cadastreSearchActive = false,
  showWorkItemCreateButton = false,
  isSidebarCollapsed = false,
  onToggleSidebar,
  gridEnabled = false,
  gridStepMeters = 50_000,
  onGridEnabledChange,
  onGridStepMetersChange,
  nomenclatureIntersectOpen = false,
  onNomenclatureIntersectToggle,
  measureMode = "line",
  onMeasureModeChange,
  contourSelectMode = "polygon",
  onContourSelectModeChange,
  contourFilterActive = false,
}: MapToolbarProps) {
  const highlightId = visualActiveTool ?? activeTool;
  const { pointPick, setPointPick, pointPickBlocked } = useGeographyFilter();
  const toolsLocked = Boolean(lockToolsForEditSession);
  const measureOpen = highlightId === "measure";
  const contourSelectOpen = highlightId === "contour-select";
  const routeOpen = highlightId === "route";

  const selectTools: ToolbarTool[] = [
    { id: "select", icon: MousePointer, label: "Выбрать" },
  ];

  const drawBasicTools: ToolbarTool[] = [
    { id: "draw-point", icon: MapPin, label: "Точка" },
    { id: "draw-line", icon: Route, label: "Линия" },
    { id: "draw-polygon", icon: Pentagon, label: "Полигон" },
    { id: "draw-rectangle", icon: Square, label: "Прямоугольник" },
    { id: "draw-circle", icon: Circle, label: "Круг" },
  ];

  const drawAdvancedTools: ToolbarTool[] = [
    { id: "draw-freehand", icon: PenLine, label: "Рисование от руки (линия/полигон)" },
    { id: "draw-angled-rectangle", icon: MoveDiagonal, label: "Прямоугольник под углом" },
    { id: "draw-sensor", icon: Target, label: "Сектор датчика (угол обзора)" },
    { id: "draw-sector", icon: Radius, label: "Сектор" },
  ];

  const measureModes: { id: MeasureMode; icon: LucideIcon; label: string }[] = [
    { id: "point", icon: MapPin, label: "Координаты точки" },
    { id: "line", icon: Route, label: "Длина линии" },
    { id: "polygon", icon: Pentagon, label: "Площадь полигона" },
    { id: "circle", icon: Circle, label: "Радиус и площадь круга" },
  ];

  const handleMeasureRulerClick = () => {
    if (toolsLocked || disabled) return;
    if (measureOpen) {
      onToolSelect("select");
      return;
    }
    onMeasureModeChange?.("line");
    onToolSelect("measure");
  };

  const handleRouteClick = () => {
    if (toolsLocked || disabled) return;
    if (routeOpen) {
      onToolSelect("select");
      return;
    }
    onToolSelect("route");
  };

  const handleMeasureModeClick = (mode: MeasureMode) => {
    if (toolsLocked || disabled) return;
    // Не закрывать при клике на уже выбранный режим (по умолчанию line —
    // иначе «Длина линии» сразу гасит измерение). Выход: линейка / Esc.
    onMeasureModeChange?.(mode);
    if (!measureOpen) onToolSelect("measure");
  };

  const contourModes: { id: ContourSelectMode; icon: LucideIcon; label: string }[] = [
    { id: "circle", icon: CircleDashed, label: "Выбор кругом" },
    { id: "polygon", icon: SquareDashed, label: "Выбор полигоном" },
  ];

  const handleContourParentClick = () => {
    if (toolsLocked || disabled) return;
    if (contourSelectOpen) {
      onToolSelect("select");
      return;
    }
    onContourSelectModeChange?.("polygon");
    onToolSelect("contour-select");
  };

  const handleContourModeClick = (mode: ContourSelectMode) => {
    if (toolsLocked || disabled) return;
    onContourSelectModeChange?.(mode);
    if (!contourSelectOpen) onToolSelect("contour-select");
  };

  const renderGroup = (tools: ToolbarTool[]) => (
    <div className="flex items-center space-x-0.5">
      {tools.map((tool) => (
        <ToolButton
          key={tool.id}
          tool={tool}
          highlighted={highlightId === tool.id}
          disabled={disabled}
          locked={toolsLocked}
          onSelect={onToolSelect}
        />
      ))}
    </div>
  );

  return (
    <div
      className={`bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-sm p-2 flex items-center border-b border-border relative z-10 ${
        disabled ? "opacity-70 pointer-events-none" : ""
      }`}
    >
      {onToggleSidebar && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onToggleSidebar}
                disabled={disabled}
                aria-label={isSidebarCollapsed ? "Показать панель" : "Скрыть панель"}
              >
                {isSidebarCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isSidebarCollapsed ? "Показать панель" : "Скрыть панель"}
            </TooltipContent>
          </Tooltip>
          <ToolbarDivider />
        </>
      )}

      {renderGroup(selectTools)}
      <ToolbarDivider />
      {renderGroup(drawBasicTools)}
      <ToolbarDivider />
      {renderGroup(drawAdvancedTools)}
      <ToolbarDivider />
      <div className="flex items-center space-x-0.5">
        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={measureOpen ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={handleMeasureRulerClick}
                disabled={disabled || toolsLocked}
                aria-label={measureOpen ? "Закрыть измерение" : "Измерить"}
                aria-pressed={measureOpen}
                aria-expanded={measureOpen}
                aria-haspopup="menu"
              >
                <Ruler className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {toolsLocked
                ? "Завершите редактирование контура в панели на карте."
                : measureOpen
                  ? "Закрыть измерение (Esc)"
                  : "Измерить: точка, линия, полигон, круг"}
            </TooltipContent>
          </Tooltip>
          {measureOpen ? (
            <div
              role="menu"
              aria-label="Режимы измерения"
              className={cn(
                "absolute left-1/2 top-full z-[220] mt-1 -translate-x-1/2",
                "flex flex-col gap-0.5 rounded-md border border-border bg-background/95 p-1 shadow-md",
                "backdrop-blur supports-[backdrop-filter]:bg-background/90",
              )}
            >
              {measureModes.map((m) => (
                <Tooltip key={m.id}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      role="menuitemradio"
                      variant={measureMode === m.id ? "secondary" : "ghost"}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleMeasureModeClick(m.id)}
                      disabled={disabled || toolsLocked}
                      aria-label={m.label}
                      aria-checked={measureMode === m.id}
                    >
                      <m.icon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {toolsLocked
                      ? `${m.label}. Завершите редактирование контура в панели на карте.`
                      : `${m.label}. Выход: линейка или Esc.`}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          ) : null}
        </div>
        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={contourSelectOpen || contourFilterActive ? "secondary" : "ghost"}
                size="icon"
                className="relative h-8 w-8"
                onClick={handleContourParentClick}
                disabled={disabled || toolsLocked}
                aria-label={
                  contourSelectOpen
                    ? "Закрыть выбор контуром"
                    : "Выбор объектов контуром"
                }
                aria-pressed={contourSelectOpen}
                aria-expanded={contourSelectOpen}
                aria-haspopup="menu"
              >
                <ScanSearch className="h-4 w-4" />
                {contourFilterActive ? (
                  <span
                    className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary"
                    aria-hidden
                  />
                ) : null}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {toolsLocked
                ? "Завершите редактирование контура в панели на карте."
                : contourSelectOpen
                  ? "Закрыть выбор контуром (Esc)"
                  : "Выбор объектов контуром"}
            </TooltipContent>
          </Tooltip>
          {contourSelectOpen ? (
            <div
              role="menu"
              aria-label="Режимы выбора контуром"
              className={cn(
                "absolute left-1/2 top-full z-[220] mt-1 -translate-x-1/2",
                "flex flex-col gap-0.5 rounded-md border border-border bg-background/95 p-1 shadow-md",
                "backdrop-blur supports-[backdrop-filter]:bg-background/90",
              )}
            >
              {contourModes.map((m) => (
                <Tooltip key={m.id}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      role="menuitemradio"
                      variant={contourSelectMode === m.id ? "secondary" : "ghost"}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleContourModeClick(m.id)}
                      disabled={disabled || toolsLocked}
                      aria-label={m.label}
                      aria-checked={contourSelectMode === m.id}
                    >
                      <m.icon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {toolsLocked
                      ? `${m.label}. Завершите редактирование контура в панели на карте.`
                      : `${m.label}. Двойной клик завершает полигон. Esc — выход.`}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          ) : null}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={routeOpen ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={handleRouteClick}
              disabled={disabled || toolsLocked}
              aria-label={routeOpen ? "Закрыть маршрут" : "Маршрут по дорогам"}
              aria-pressed={routeOpen}
            >
              <Navigation className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {toolsLocked
              ? "Завершите редактирование контура в панели на карте."
              : routeOpen
                ? "Закрыть маршрут (Esc)"
                : "Маршрут по дорогам (OSRM): A→B и ближайшие"}
          </TooltipContent>
        </Tooltip>
        <ToolButton
          tool={{ id: "search", icon: Search, label: "Поиск на карте" }}
          highlighted={highlightId === "search"}
          disabled={disabled}
          locked={toolsLocked}
          onSelect={onToolSelect}
        />
        <ToolButton
          tool={{
            id: "goto-coordinates",
            icon: LocateFixed,
            label: "Поиск точки по координатам",
          }}
          highlighted={highlightId === "goto-coordinates"}
          disabled={disabled}
          locked={toolsLocked}
          onSelect={onToolSelect}
        />
        {showWorkItemCreateButton ? (
          <ToolButton
            tool={{
              id: "work-item-create",
              icon: ClipboardPlus,
              label: "Создать задачу по точке",
            }}
            highlighted={highlightId === "work-item-create"}
            disabled={disabled}
            locked={toolsLocked}
            onSelect={onToolSelect}
          />
        ) : null}
      </div>

      {onGridEnabledChange ? (
        <>
          <ToolbarDivider />
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={gridEnabled ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onGridEnabledChange(!gridEnabled)}
                  disabled={disabled}
                  aria-label={gridEnabled ? "Скрыть сетку" : "Показать сетку"}
                  aria-pressed={gridEnabled}
                  title={gridEnabled ? "Скрыть сетку разграфки" : "Показать сетку разграфки"}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {gridEnabled ? "Скрыть сетку разграфки" : "Показать сетку разграфки"}
              </TooltipContent>
            </Tooltip>
            <select
              className={cn(
                "h-8 max-w-[5.75rem] rounded-md border border-input bg-background px-1.5 text-[11px]",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                disabled && "opacity-50 pointer-events-none",
              )}
              value={gridStepMeters}
              title="Шаг разграфки"
              aria-label="Шаг разграфки"
              disabled={disabled}
              onChange={(event) =>
                onGridStepMetersChange?.(
                  Number(event.target.value) as OiMapGridStepMeters,
                )
              }
            >
              {OI_MAP_GRID_STEP_PRESETS.map((step) => (
                <option key={step} value={step}>
                  {formatOiMapGridStepLabel(step)}
                </option>
              ))}
            </select>
            {onNomenclatureIntersectToggle ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={nomenclatureIntersectOpen ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={onNomenclatureIntersectToggle}
                    disabled={disabled}
                    aria-label={
                      nomenclatureIntersectOpen
                        ? "Скрыть пересечения с разграфкой"
                        : "Пересечения с разграфкой"
                    }
                    aria-pressed={nomenclatureIntersectOpen}
                    title="Пересечения объектов с номенклатурной разграфкой"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {nomenclatureIntersectOpen
                    ? "Скрыть панель пересечений с разграфкой"
                    : "Пересечения с разграфкой (номенклатура)"}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </>
      ) : onNomenclatureIntersectToggle ? (
        <>
          <ToolbarDivider />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={nomenclatureIntersectOpen ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={onNomenclatureIntersectToggle}
                disabled={disabled}
                aria-label={
                  nomenclatureIntersectOpen
                    ? "Скрыть пересечения с разграфкой"
                    : "Пересечения с разграфкой"
                }
                aria-pressed={nomenclatureIntersectOpen}
                title="Пересечения объектов с номенклатурной разграфкой"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {nomenclatureIntersectOpen
                ? "Скрыть панель пересечений с разграфкой"
                : "Пересечения с разграфкой (номенклатура)"}
            </TooltipContent>
          </Tooltip>
        </>
      ) : null}

      <ToolbarDivider />
      <div className="flex items-center space-x-0.5">
        {onImportCoords ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onImportCoords}
                disabled={disabled || toolsLocked}
                aria-label="Импорт координат"
              >
                <FileInput className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {toolsLocked
                ? "Завершите редактирование контура в панели на карте."
                : "Импорт координат (текст или файл)"}
            </TooltipContent>
          </Tooltip>
        ) : null}
        {onCadastreSearch ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={cadastreSearchActive ? "secondary" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={onCadastreSearch}
                disabled={disabled}
                aria-label="Поиск кадастров"
                aria-pressed={cadastreSearchActive}
              >
                <LandPlot className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Поиск кадастров</TooltipContent>
          </Tooltip>
        ) : null}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={pointPick ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={() => setPointPick(!pointPick)}
              disabled={disabled || pointPickBlocked || toolsLocked}
              aria-label="Указать на карте"
              aria-pressed={pointPick}
              title={
                pointPickBlocked || toolsLocked
                  ? "Сначала завершите чертёж"
                  : "Указать территорию на карте"
              }
            >
              <MousePointerClick className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {pointPick
              ? "Указать территорию: кликните по карте. Esc — отмена."
              : "Указать территорию на карте. Глубина задаётся в географических фильтрах."}
          </TooltipContent>
        </Tooltip>
      </div>

      {onDownload ? (
        <>
          <ToolbarDivider />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onDownload}
                disabled={disabled}
                aria-label="Экспорт карты"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Экспорт карты (PNG/PDF)</TooltipContent>
          </Tooltip>
        </>
      ) : null}
    </div>
  );
}
